<template>
  <div>
    <validation-observer ref="group">
      <v-form v-if="form" @submit.prevent="save" ref="form">
        <v-row>
          <v-col>
            <validation-provider
              rules="required"
              name="name"
              v-slot="{ errors }"
            >
              <v-text-field
                label="Group Name"
                v-model="form.name"
                :error-messages="errors"
                outlined
                required
              ></v-text-field>
              <div
                v-if="errors.length"
                class="v-messages theme--light error--text mt-3 pl-3"
              >
                <div class="v-messages__wrapper">
                  <div class="v-messages__message">
                    {{ errors[0] }}
                  </div>
                </div>
              </div>
            </validation-provider>

            <div class="mb-5">
              <div class="text-h5 mt-5 mb-3">About</div>
              <validation-provider
                rules="required"
                name="about"
                v-slot="{ errors }"
              >
                <quill-editor v-model="form.about" :error-messages="errors" />
                <div
                  v-if="errors.length"
                  class="v-messages theme--light error--text mt-3 pl-3"
                >
                  <div class="v-messages__wrapper">
                    <div class="v-messages__message">
                      {{ errors[0] }}
                    </div>
                  </div>
                </div>
              </validation-provider>
            </div>

            <div class="mb-5">
              <div class="text-h5 mt-5 mb-3">Vision</div>
              <validation-provider
                rules="required"
                name="vision"
                v-slot="{ errors }"
              >
                <quill-editor v-model="form.vision" :error-messages="errors" />
                <div
                  v-if="errors.length"
                  class="v-messages theme--light error--text mt-3 pl-3"
                >
                  <div class="v-messages__wrapper">
                    <div class="v-messages__message">
                      {{ errors[0] }}
                    </div>
                  </div>
                </div>
              </validation-provider>
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <div>
              <input :value="groupCover" hidden />
              <v-img
                size="100%"
                class="mb-4"
                v-if="groupCover"
                :src="groupCover"
                :lazy-src="groupCover"
              ></v-img>
              <v-btn @click="addCover" color="primary">
                {{ groupCover ? 'Change' : 'Add' }} cover image
              </v-btn>
              <div
                v-if="errors.length"
                class="v-messages theme--light error--text mt-3 pl-3"
              >
                <div class="v-messages__wrapper">
                  <div class="v-messages__message">
                    {{ errors[0] }}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div class="text-h5 mt-5 mb-3">Group Location</div>
              <validation-provider
                rules="required"
                name="address"
                v-slot="{ errors }"
              >
                <v-autocomplete
                  v-model="items[0]"
                  :items="items"
                  :loading="isSearchingAddress"
                  :search-input.sync="searchAddress"
                  color="primary"
                  hide-no-data
                  hide-selected
                  item-text="place_name"
                  item-value="place_name"
                  placeholder="Search address"
                  return-object
                  outlined
                ></v-autocomplete>
                <div
                  v-if="errors.length"
                  class="v-messages theme--light error--text mt-3 pl-3"
                >
                  <div class="v-messages__wrapper">
                    <div class="v-messages__message">
                      {{ errors[0] }}
                    </div>
                  </div>
                </div>
              </validation-provider>
            </div>

            <div>
              <div class="text-h5 mt-5 mb-3">Values</div>
              <v-text-field
                outlined
                :key="key"
                v-model="values[key].values"
                v-for="(value, key) in values"
              ></v-text-field>
            </div>
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <v-btn
              color="primary"
              class="mr-4"
              :loading="isLoading"
              @click="updateGroupDetails"
            >
              Update
            </v-btn>
          </v-col>
        </v-row>

        <!-- Image cropper component -->
        <image-cropper
          v-model="showImageUpload"
          :aspect-ratio="16 / 9"
          @change="imageChanged"
        />
      </v-form>
    </validation-observer>

    <div class="mt-16" v-if="group">
      <div class="text-h5 mt-5 mb-3">
        Group Members ({{ group.members_count }})
      </div>
      <v-simple-table>
        <thead>
          <tr>
            <th width="1%"></th>
            <th class="text-left">Name</th>
            <th class="text-left">Email</th>
            <th class="text-left">Role</th>
            <th>&nbsp;</th>
          </tr>
        </thead>
        <tbody>
          <group-members
            v-for="member in group.members"
            :key="member.id"
            :member="member"
            :group-id="group.id"
            :owner-id="group.group_owner_id"
            @removed="onMemberRemoved"
          >
          </group-members>
        </tbody>
      </v-simple-table>
    </div>
  </div>
</template>

<script>
// import Form from '@/utils/form'
import Group from '@/models/Group'
import { cloneDeep, map, debounce, each } from 'lodash'
import QuillEditor from '@/components/QuillEditor'
import GroupMembers from '@/components/group/GroupMembers'
import ImageCropper from '@/components/modals/ImageCropper'

export default {
  components: {
    QuillEditor,
    ImageCropper,
    GroupMembers
  },

  props: {
    group: {
      type: Group,
      default() {
        return new Group({
          name: '',
          about: '',
          vision: '',
          cover: null,
          group_owner_id: 0,
          members: [],
          members_count: 0
        })
      }
    }
  },

  data() {
    return {
      form: null,
      showImageUpload: false,
      cover: null,
      errors: [],
      isLoading: false,
      descriptionLimit: 60,
      entries: [],
      isSearchingAddress: false,
      addressModel: null,
      searchAddress: null,
      newCover: null
    }
  },

  mounted() {
    this.setForm(this.group)
  },

  computed: {
    groupCover() {
      if (this.cover) {
        return this.cover
      }

      if (this.group.cover) {
        return this.group.cover.url
      }

      return null
    },

    values: {
      get() {
        let values = this.group ? this.group.values || [] : []
        values.length = 5
        values = map(values, value => {
          return {
            values: '',
            ...value
          }
        })

        return values
      },
      set() {}
    },
    items() {
      if (this.entries.length > 0) {
        return this.entries
      }

      return [{ place_name: this.group.address }]
    }
  },

  methods: {
    setForm(group) {
      if (group) {
        this.form = cloneDeep(group)
      }
    },
    setErrors(errors) {
      this.$refs.blog.setErrors(errors)
    },
    addCover() {
      this.showImageUpload = true
    },
    imageChanged(image) {
      const reader = new FileReader()
      reader.readAsDataURL(image)
      reader.onload = e => {
        this.cover = reader.result
        this.form.cover = image
        this.newCover = image
      }
    },
    updateGroupDetails() {
      this.isLoading = true
      const { name, about, vision } = this.form
      const formData = new FormData()

      if (this.group.id) {
        formData.append('_method', 'PUT')
      }

      each(
        {
          name,
          about,
          vision,
          address: this.items[0].place_name,
          cover: this.newCover,
          values: JSON.stringify(this.values)
        },
        (value, key) => {
          if (value) {
            formData.append(key, value)
          }
        }
      )

      this.$api
        .post(`/groups/${this.group.id}`, formData)
        .then(() => {
          this.$toast('Group has been successfully updated!', {
            color: 'green'
          })
        })
        .catch(({ response }) =>
          this.$refs.group.setErrors(response.data.errors)
        )
        .finally(() => (this.isLoading = false))
    },
    onMemberRemoved() {
      Group.$find(this.group.id).then(
        group => (this.group.members = group.members)
      )
    }
  },

  watch: {
    group(group) {
      this.setForm(group)
    },
    values() {
      this.form.values = this.values
    },
    searchAddress: debounce(function(address) {
      if (this.isSearchingAddress || !address) return

      if (address === this.items[0]) return

      this.isSearchingAddress = true

      this.$api
        .get('/group-address', {
          params: {
            address
          }
        })
        .then(({ data }) => {
          this.entries = data.data
        })
        .finally(() => (this.isSearchingAddress = false))
    }, 500)
  }
}
</script>

<style>
.ql-container {
  max-height: 300px;
}
</style>
